import React, { useEffect, useState, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import CardWrapper from './CardWrapper';

const COLORS = ['#00C49F', '#FF4C4C'];
const API_URL = process.env.REACT_APP_API_URL;

export default function WinLossPieChart({ onError }) {
  const [data, setData] = useState([
    { name: 'Wins', value: 0 },
    { name: 'Losses', value: 0 },
  ]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const abortRef = useRef(null);
  const lastFetchAt = useRef(0);

  const fetchWinLoss = useCallback(async () => {
    try {
      const now = Date.now();
      if (now - lastFetchAt.current < 150) return;
      lastFetchAt.current = now;

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setErr(null);

      const token = localStorage.getItem('fq_token');
      const res = await fetch(`${API_URL}/api/analytics/winloss`, {
        credentials: 'omit',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      let formatted;
      if (json && Array.isArray(json.breakdown)) {
        formatted = json.breakdown.map((x) => ({
          name: x.name,
          value: Number(x.value ?? 0),
        }));
      } else if (Array.isArray(json)) {
        const wins = Number(json.find((x) => x.name === 'Wins')?.value ?? 0);
        const losses = Number(json.find((x) => x.name === 'Losses')?.value ?? 0);
        formatted = [
          { name: 'Wins', value: wins },
          { name: 'Losses', value: losses },
        ];
      } else {
        const wins = Number(json?.wins ?? 0);
        const losses = Number(json?.losses ?? 0);
        formatted = [
          { name: 'Wins', value: wins },
          { name: 'Losses', value: losses },
        ];
      }

      setData(formatted);
      if (onError) onError(null);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('[WinLossPieChart] fetch failed:', e);
      setErr(e.message || 'Failed to load');
      setData([
        { name: 'Wins', value: 0 },
        { name: 'Losses', value: 0 },
      ]);
      if (onError) onError(e);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [onError]);

  useEffect(() => {
    fetchWinLoss();

    const onJournalUpdated = () => fetchWinLoss();
    window.addEventListener('journal:updated', onJournalUpdated);

    let bc;
    try {
      bc = new BroadcastChannel('fq-analytics');
      bc.onmessage = (e) => {
        if (e?.data?.type === 'analytics:update') fetchWinLoss();
      };
    } catch {
      // safe to ignore
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchWinLoss();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('journal:updated', onJournalUpdated);
      document.removeEventListener('visibilitychange', onVisible);
      try {
        bc && bc.close();
      } catch {}
      try {
        abortRef.current && abortRef.current.abort();
      } catch {}
    };
  }, [fetchWinLoss]);

  const wins = Number(data?.find((d) => d.name === 'Wins')?.value ?? 0);
  const losses = Number(data?.find((d) => d.name === 'Losses')?.value ?? 0);
  const total = wins + losses;

  return (
    <CardWrapper>
      <div className="mx-auto w-full max-w-3xl px-2 sm:px-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-base md:text-lg font-semibold text-center flex-1">
            Win vs Loss Breakdown
          </h2>
          <button
            onClick={fetchWinLoss}
            disabled={loading}
            className="text-[11px] rounded px-2 py-1 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-60"
            title="Refresh"
          >
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        {loading ? (
          <p className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5 text-center text-sm text-gray-400">
            Loading win/loss data…
          </p>
        ) : err ? (
          <p className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5 text-center text-sm text-red-400">
            Couldn’t load — showing zero state.
          </p>
        ) : total === 0 ? (
          <p className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5 text-center text-sm text-gray-400">
            No closed trades yet. Add a journal entry to populate this chart.
          </p>
        ) : (
          <div className="space-y-4 rounded-xl border border-[#2a2a2a]/40 bg-[rgba(18,18,18,0.75)] p-4 text-white shadow-[0_0_12px_rgba(0,0,0,0.4)] backdrop-blur-sm transition-all duration-300 sm:p-4 lg:p-5">
            <div className="w-full h-44 sm:h-48 lg:h-52">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={68}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {data.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f1f1f',
                      border: 'none',
                      borderRadius: '10px',
                    }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#e5e7eb' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="flex items-center justify-center gap-5 text-xs md:text-sm text-gray-400">
              {data.map((item, index) => (
                <div key={item.name} className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span>
                    {item.name}: <span className="text-white">{item.value}</span>
                  </span>
                </div>
              ))}
            </div>

            <div className="text-center text-xs md:text-sm text-neutral-300">
              Total: {total} · Wins: {wins} · Losses: {losses}
            </div>
          </div>
        )}
      </div>
    </CardWrapper>
  );
}